// Ficha 360 del crédito — COMPONENTE COMPARTIDO. Espejo del shared `ficha-credito.js` del mock:
// hero + 6 pestañas. Recibe SOLO el `creditId` y trae sus propios datos, para que Cartera, Mis
// ventas y Clientes la monten igual sin pasarle nada más.
//
// Uso:
//   import { CreditFile } from '@/domains/credit';
//   <CreditFile creditId="01M0V2PK0ENHNX9ZNXDJZDCH6C" />
//
// Cada bloque falla por separado: hoy el rol del Portal Admin SK tiene los permisos `mdm.*` y no
// los `loan.*`, así que Dispositivo renderiza con dato real mientras el resto muestra su 403.
import { useMemo, useState } from 'react';
import { FileText } from 'lucide-react';
import { MasterDetailEmpty } from '@/shared/patterns/MasterDetail';
import { FileDrawerTabs } from '@/shared/patterns/FileDrawer';
import { cn } from '@/lib/cn';
import { mxDay } from '@/lib/dateHelpers';
import { useCreditFile } from './api/useCreditFile';
import { resolveArrearsDays } from './detailHeader';
import { useCreditSaleLines } from './api/useCreditSaleLines';
import { useCreditPayments } from '../installments/api/useCreditPayments';
import { CreditBlockError, CreditBlockLoading } from './components/CreditFileUi';
import { CreditDeviceTab } from './components/CreditDeviceTab';
import { CreditDocsTab } from './components/CreditDocsTab';
import { CreditDetailHeader } from './components/CreditDetailHeader';
import { CreditPrepaymentAction } from './components/CreditPrepaymentAction';
import { CreditHistoryTab } from './components/CreditHistoryTab';
import { CreditPaymentsTab } from './components/CreditPaymentsTab';
import { CreditScheduleTab } from './components/CreditScheduleTab';
import { CreditSummaryTab, type CreditSummaryCredit } from './components/CreditSummaryTab';

export const CREDIT_FILE_TABS = [
    'summary',
    'schedule',
    'payments',
    'device',
    'docs',
    'history',
] as const;

export type CreditFileTab = (typeof CREDIT_FILE_TABS)[number];

const TAB_LABEL: Record<CreditFileTab, string> = {
    summary: 'Resumen',
    schedule: 'Amortización',
    payments: 'Pagos',
    device: 'Dispositivo',
    docs: 'Docs',
    history: 'Historial',
};

/** Un valor desconocido cae en «Resumen» en vez de dejar la ficha en blanco. */
export function parseCreditFileTab(raw: string | undefined): CreditFileTab {
    return CREDIT_FILE_TABS.includes(raw as CreditFileTab) ? (raw as CreditFileTab) : 'summary';
}

export interface CreditFileProps {
    /** `null` pinta el estado vacío: es el panel derecho sin nada seleccionado. */
    creditId: string | null;
    /** Pestaña con la que abre. Por defecto, Resumen. */
    initialTab?: CreditFileTab;
    className?: string;
}

export function CreditFile({ creditId, initialTab = 'summary', className }: CreditFileProps) {
    if (!creditId) {
        return (
            <MasterDetailEmpty
                icon={<FileText className="size-10" aria-hidden />}
                title="Selecciona un crédito"
                sub="Elige un crédito de la lista para ver su expediente completo."
            />
        );
    }
    return (
        <LoadedCreditFile
            key={creditId}
            creditId={creditId}
            initialTab={initialTab}
            className={className}
        />
    );
}

function LoadedCreditFile({
    creditId,
    initialTab,
    className,
}: {
    creditId: string;
    initialTab: CreditFileTab;
    className?: string;
}) {
    const [tab, setTab] = useState<CreditFileTab>(initialTab);
    const data = useCreditFile(creditId);
    // El silo puede omitir `saleContext` (créditos viejos): se lee como el agregado opcional que es.
    const saleContext = (data.credit.data as CreditSummaryCredit | undefined)?.saleContext;
    const saleLines = useCreditSaleLines(creditId, saleContext?.retailCustomerId);
    // Misma consulta que la pestaña Pagos: el encabezado toma el pago más reciente del libro.
    const payments = useCreditPayments(creditId);
    const lastPayment = {
        data:
            payments.isLoading || payments.failure ? undefined : (payments.payments.at(0) ?? null),
        isLoading: payments.isLoading,
        failure: payments.failure,
        retry: payments.retry,
    };
    // Día de referencia de la mora, fijo mientras la ficha está abierta: recalcularlo en cada
    // render movería los días de atraso bajo la vista del operador.
    const today = useMemo(() => mxDay(new Date().toISOString()), []);

    const { credit } = data;

    if (credit.isLoading) {
        return (
            <div className={cn('flex min-h-0 flex-1 items-center justify-center', className)}>
                <CreditBlockLoading label="Cargando el crédito…" />
            </div>
        );
    }

    // Sin el crédito no hay hero ni Resumen. Dispositivo e Historial SÍ tienen fuente propia, así
    // que la ficha sigue en pie y solo se recorta a lo que se pudo leer.
    if (credit.failure !== null || !credit.data) {
        return (
            <div className={cn('flex min-h-0 flex-1 flex-col overflow-y-auto p-3', className)}>
                <CreditBlockError
                    failure={credit.failure ?? 'not-found'}
                    subject="este crédito"
                    onRetry={credit.retry}
                />
                <div className="mt-2">
                    <CreditDeviceTab device={data.device} operations={data.operations} />
                </div>
            </div>
        );
    }

    const creditData = credit.data;

    return (
        <div className={cn('flex min-h-0 flex-1 flex-col', className)}>
            <CreditDetailHeader
                creditId={creditId}
                credit={creditData}
                device={data.device}
                installments={data.installments}
                lastPayment={lastPayment}
                today={today}
                idAction={<CreditPrepaymentAction creditId={creditId} status={creditData.status} />}
            />

            {/* Una sola fila: si no entra scrollea con sombra en el borde, sin partir pestañas. */}
            <FileDrawerTabs
                tabs={CREDIT_FILE_TABS.map((key) => ({ key, label: TAB_LABEL[key] }))}
                activeTab={tab}
                onChange={(key) => {
                    setTab(parseCreditFileTab(key));
                }}
            />

            <div className="min-h-0 flex-1 overflow-y-auto p-2.5">
                {tab === 'summary' ? (
                    <CreditSummaryTab
                        credit={creditData}
                        arrearsDays={resolveArrearsDays(
                            creditData.overdueDays,
                            data.installments.data,
                            today,
                        )}
                        device={data.device}
                        saleLines={saleLines}
                    />
                ) : null}
                {tab === 'schedule' ? (
                    <CreditScheduleTab installments={data.installments} today={today} />
                ) : null}
                {tab === 'payments' ? <CreditPaymentsTab creditId={creditId} /> : null}
                {tab === 'device' ? (
                    <CreditDeviceTab device={data.device} operations={data.operations} />
                ) : null}
                {tab === 'docs' ? (
                    <CreditDocsTab creditId={creditId} contracts={data.contracts} />
                ) : null}
                {tab === 'history' ? (
                    <CreditHistoryTab
                        credit={creditData}
                        installments={data.installments}
                        device={data.device}
                        today={today}
                    />
                ) : null}
            </div>
        </div>
    );
}
